import { USER_ROLE } from '@/entities/user/constants';
import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import type {
  GetWorkspaceUsageResponse,
  Workspace,
  WorkspaceSettingPayload,
} from '@/entities/workspace/types';

type InviteCodeMap = Partial<Record<USER_ROLE, string>>;

const BASE_WORKSPACE: Workspace = {
  id: '1',
  name: 'Syncly Labs',
  language: 'en',
  timezone: 'America/Los_Angeles',
  plan: WORKSPACE_PLAN.BASIC,
  constraints: {
    campaign_slot_limit: 10,
    campaign_item_slot_limit: 500,
    campaign_tracking_slot_limit: 500,
    query_count: 200,
    post_limit_count: 1_000,
    influencer_search_results_limit: 50,
    influencer_monthly_export_creators_limit: 100,
    influencer_saved_lists_limit: 3,
  },
  configuration: {
    public_query_visibility: false,
  },
  privilege: USER_ROLE.OWNER,
  plan_expired_at: 1_924_992_000,
};

const MOCK_USAGE_SEED = {
  queryCount: 24,
  influencerMonthlyExportCreators: 18,
  influencerSavedLists: 2,
} as const;

let mockWorkspace: Workspace = structuredClone(BASE_WORKSPACE);
let mockInviteCodes: InviteCodeMap = {
  [USER_ROLE.MANAGER]: 'invite-manager-seed',
  [USER_ROLE.VIEWER]: 'invite-viewer-seed',
};

export function initMockWorkspaceDB() {
  mockWorkspace = structuredClone(BASE_WORKSPACE);
  mockInviteCodes = {
    [USER_ROLE.MANAGER]: 'invite-manager-seed',
    [USER_ROLE.VIEWER]: 'invite-viewer-seed',
  };
}

export function getMockWorkspace() {
  return structuredClone(mockWorkspace);
}

export function getMockWorkspaceSummary() {
  const { id, name, privilege, plan_expired_at } = mockWorkspace;
  return {
    id,
    name,
    privilege,
    plan_expired_at,
  };
}

export function getMockWorkspaceUsage() {
  const { query_count, influencer_monthly_export_creators_limit, influencer_saved_lists_limit } =
    mockWorkspace.constraints;

  const usage: GetWorkspaceUsageResponse = {
    query_count: {
      used: MOCK_USAGE_SEED.queryCount,
      limit: query_count,
      remaining: query_count - MOCK_USAGE_SEED.queryCount,
    },
    influencer_monthly_export_creators: {
      used: MOCK_USAGE_SEED.influencerMonthlyExportCreators,
      limit: influencer_monthly_export_creators_limit,
      remaining:
        influencer_monthly_export_creators_limit - MOCK_USAGE_SEED.influencerMonthlyExportCreators,
    },
    influencer_saved_lists: {
      used: MOCK_USAGE_SEED.influencerSavedLists,
      limit: influencer_saved_lists_limit,
      remaining: influencer_saved_lists_limit - MOCK_USAGE_SEED.influencerSavedLists,
    },
  };

  return usage;
}

export function isMockWorkspaceNameTaken(name: string) {
  return mockWorkspace.name.toLowerCase() === name.trim().toLowerCase();
}

export function updateMockWorkspaceSettings(payload: WorkspaceSettingPayload) {
  mockWorkspace = {
    ...mockWorkspace,
    ...payload,
  };

  return getMockWorkspace();
}

export function getMockInviteCode(role: USER_ROLE) {
  return mockInviteCodes[role] ?? null;
}

export function regenerateMockInviteCode(role: USER_ROLE) {
  const code = `invite-${role}-${crypto.randomUUID().slice(0, 8)}`;
  mockInviteCodes = {
    ...mockInviteCodes,
    [role]: code,
  };

  return code;
}
